// screens/NewOrderScreen.js - Screen 3
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { useLang } from '../contexts/LanguageContext';
import { ConfirmDialog, SyncBar, useSmartBack } from '../components/shared';
import { BottomNav } from './HomeScreen';
import { useSync } from '../contexts/SyncContext';
import { queueOrder, queueSession, requestBackgroundSync } from '../utils/offlineQueue';
import { filterModels, readCachedModels, writeCachedModels } from '../utils/modelSearch';
import api, { getErrorMessage } from '../utils/api';

function genId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID();
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    return (c === 'x' ? r : (r & 0x3) | 0x8).toString(16);
  });
}

export default function NewOrderScreen() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const goBack = useSmartBack();
  const toast = useToast();
  const { isOnline } = useSync();
  const { t } = useLang();

  const [orderNumber, setOrderNumber] = useState('');
  const [productName, setProductName] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  const [checkingPhotos, setCheckingPhotos] = useState(false);
  const [existingWarning, setExistingWarning] = useState(null);
  // Product/model selection: pick from the admin-curated list, or tick
  // "undocumented" to type a one-off name (not added to the list).
  // The list starts from the local copy so searching works offline.
  const [models, setModels] = useState(readCachedModels);
  const [modelQuery, setModelQuery] = useState('');
  const [undocumented, setUndocumented] = useState(false);

  useEffect(() => {
    api.get('/api/product-models')
      .then(({ data }) => { setModels(data); writeCachedModels(data); })
      .catch(() => {});
  }, []);

  const modelHits = filterModels(models, modelQuery);

  const pickModel = (m) => { setProductName(m.name); setModelQuery(m.name); };

  const handleConfirmOrder = () => {
    if (!orderNumber.trim()) return;
    setConfirmed(true);
  };

  const handleEdit = () => {
    setConfirmed(false);
    setProductName('');
    setModelQuery('');
    setUndocumented(false);
  };

  // Offline: mint ids locally, queue the order + session for sync, and head
  // straight into the capture flow so a missing network never blocks shooting.
  // Factory comes from the chosen model (null for undocumented models).
  const selectedFactory = undocumented ? null : (models.find(m => m.name === productName)?.factory || null);

  const startOffline = async () => {
    const orderId = genId();
    const sessionId = genId();
    const order_number = orderNumber.trim();
    const product_name = productName.trim();
    await queueOrder({ id: orderId, order_number, product_name, factory: selectedFactory });
    await queueSession({ id: sessionId, order_id: orderId });
    requestBackgroundSync();
    toast.info(t('order.offline.saved'));
    navigate(`/guided-photo/${orderId}/${sessionId}`, { state: { order_number, product_name } });
  };

  const handleStart = async () => {
    if (!productName.trim()) { toast.error(t('order.productrequired')); return; }
    setLoading(true);

    if (!isOnline || !navigator.onLine) {
      try { await startOffline(); }
      catch { toast.error('Could not start offline session'); }
      finally { setLoading(false); }
      return;
    }

    try {
      // First create or find the order
      const { data: order } = await api.post('/api/orders', {
        order_number: orderNumber.trim(),
        product_name: productName.trim(),
        factory: selectedFactory,
      });

      // Check for existing photos
      setCheckingPhotos(true);
      const { data: photoCheck } = await api.get(`/api/orders/${order.id}/has-photos`);
      setCheckingPhotos(false);

      if (photoCheck.has_photos) {
        setExistingWarning({ orderId: order.id });
      } else {
        startSession(order.id);
      }
    } catch (err) {
      // Network dropped between confirming and starting: fall back to offline.
      if (!err?.response) {
        try { await startOffline(); return; } catch {}
      }
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
      setCheckingPhotos(false);
    }
  };

  const startSession = async (orderId) => {
    try {
      const { data: session } = await api.post('/api/sessions', { order_id: orderId });
      navigate(`/guided-photo/${orderId}/${session.id}`);
    } catch (err) {
      toast.error('Failed to start session');
    }
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', minHeight: '100vh', minHeight: '100dvh' }}>
      <SyncBar />
      <div className="page-header">
        <button className="btn btn-ghost btn-icon" onClick={goBack}>←</button>
        <h2>{t('order.new')}</h2>
        <div style={{ width: 52 }} />
      </div>

      <div className="page-content" style={{ display: 'flex', flexDirection: 'column', gap: 24, maxWidth: 480, margin: '0 auto', width: '100%' }}>

        {/* Step 1: Order number */}
        <div className="card">
          <div className="section-title">{t('order.step1')}</div>
          <div className="form-group" style={{ marginBottom: 16 }}>
            <input
              type="text"
              value={orderNumber}
              onChange={e => { setOrderNumber(e.target.value); setConfirmed(false); }}
              placeholder={t('order.scan')}
              className="input-field"
              style={{ fontSize: '1.125rem', letterSpacing: '0.02em' }}
              disabled={confirmed}
              autoFocus
            />
            <div className="form-hint">{t('order.novalidation')}</div>
          </div>

          {/* Large display confirmation */}
          {orderNumber && (
            <div style={{ marginBottom: 16 }}>
              <div className="form-label" style={{ marginBottom: 8 }}>{t('order.confirmnumber')}</div>
              <div className="order-number-display">{orderNumber.trim()}</div>
            </div>
          )}

          {!confirmed ? (
            <button
              className="btn btn-primary btn-full btn-lg"
              onClick={handleConfirmOrder}
              disabled={!orderNumber.trim()}
            >
              ✓ {t('order.confirm')}
            </button>
          ) : (
            <div style={{ display: 'flex', gap: 10 }}>
              <button className="btn btn-secondary" onClick={handleEdit}>✎ {t('order.edit')}</button>
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 8 }}>
                <div style={{ width: 10, height: 10, borderRadius: '50%', background: 'var(--accent-success)', flexShrink: 0 }} />
                <span style={{ fontSize: '0.875rem', color: 'var(--accent-success)', fontWeight: 600 }}>{t('order.confirmed')}</span>
              </div>
            </div>
          )}
        </div>

        {/* Step 2: Product name */}
        {confirmed && (
          <div className="card" style={{ animation: 'slideUp 0.2s ease' }}>
            <div className="section-title">{t('order.step2')}</div>

            {!undocumented ? (
              <div className="form-group" style={{ marginBottom: 12 }}>
                <input
                  type="search"
                  className="input-field"
                  value={modelQuery}
                  onChange={e => { setModelQuery(e.target.value); setProductName(''); }}
                  onKeyDown={e => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      if (!productName && modelHits.length > 0) pickModel(modelHits[0]);
                    }
                  }}
                  placeholder={t('order.model.search')}
                  aria-label={t('order.model.search')}
                  enterKeyHint="done"
                  autoComplete="off"
                  autoCorrect="off"
                  autoCapitalize="none"
                  spellCheck={false}
                  style={{ fontSize: '1.0625rem' }}
                  autoFocus
                />
                {!productName && modelHits.length > 0 && (
                  <div
                    role="listbox"
                    style={{
                      marginTop: 8, maxHeight: '40vh', overflowY: 'auto',
                      border: '1px solid var(--border-accent)', borderRadius: 'var(--radius-md)'
                    }}
                  >
                    {modelHits.map((m, i) => (
                      <button
                        key={m.id}
                        type="button"
                        role="option"
                        aria-selected={false}
                        onClick={() => pickModel(m)}
                        style={{
                          display: 'block', width: '100%', textAlign: 'left',
                          padding: '12px 14px', fontSize: '1.0625rem', minHeight: 44,
                          background: i === 0 && modelQuery.trim() ? 'rgba(79,142,247,0.10)' : 'transparent',
                          color: 'inherit', border: 'none', borderBottom: '1px solid var(--border-accent)',
                          cursor: 'pointer'
                        }}
                      >
                        {m.name}
                      </button>
                    ))}
                  </div>
                )}
                {!productName && models.length > 0 && modelHits.length === 0 && (
                  <div className="form-hint" role="status" style={{ marginTop: 8, color: 'var(--accent-danger)' }}>
                    {t('order.model.nomatch')}
                  </div>
                )}
                <div className="form-hint">
                  {models.length === 0 ? t('order.nomodels') : (productName ? t('order.model.selected') : t('order.model.choose'))}
                </div>
              </div>
            ) : (
              <div className="form-group" style={{ marginBottom: 12 }}>
                <input
                  type="text"
                  className="input-field"
                  value={productName}
                  onChange={e => setProductName(e.target.value)}
                  placeholder={t('order.model.nameph')}
                  style={{ fontSize: '1.0625rem' }}
                  autoFocus
                />
                <div className="form-hint">{t('order.model.namerequired')}</div>
              </div>
            )}

            {/* Undocumented model toggle */}
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={undocumented}
                onChange={e => { setUndocumented(e.target.checked); setProductName(''); setModelQuery(''); }}
                style={{ width: 18, height: 18, flexShrink: 0 }}
              />
              <span style={{ fontSize: '0.9375rem' }}>{t('order.undocumented')}</span>
            </label>

            <button
              className="btn btn-primary btn-full btn-xl"
              onClick={handleStart}
              disabled={!productName.trim() || loading || checkingPhotos}
              style={{ marginTop: 8 }}
            >
              {loading || checkingPhotos ? (
                <><div className="spinner" style={{ width: 18, height: 18 }} /> {t('order.starting')}</>
              ) : (
                <>📸 {t('order.start')}</>
              )}
            </button>
          </div>
        )}

        {/* Tip */}
        <div style={{
          padding: '12px 16px', background: 'rgba(79,142,247,0.06)',
          border: '1px solid var(--border-accent)', borderRadius: 'var(--radius-md)',
          fontSize: '0.8125rem', color: 'var(--text-muted)'
        }}>
          💡 <strong>Tip:</strong> Wrong session? Complete the new correct session. Old sessions are kept as historical records and cannot be deleted from within the app.
        </div>
      </div>

      {/* Warning: existing photos */}
      <ConfirmDialog
        open={!!existingWarning}
        title="⚠ Existing Photos Found"
        message="This order already has photos. Do you want to start a new session? All previous sessions will be kept."
        confirmLabel="Start New Session"
        onConfirm={() => { setExistingWarning(null); startSession(existingWarning.orderId); }}
        onCancel={() => { setExistingWarning(null); navigate('/search'); }}
      />

      <BottomNav active="new" />
    </div>
  );
}
